import {
  BreakpointObserver,
  Breakpoints,
  BreakpointState,
} from '@angular/cdk/layout';

import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  input,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  MAT_CHECKBOX_DEFAULT_OPTIONS,
  MatCheckboxDefaultOptions,
} from '@angular/material/checkbox';
import { Person } from '@core/data/persons/person.model';
import { DataModel } from '@core/data/data.model';
import { QuestionnairesService } from '@core/data/questionnaires/questionnaires.service';
import { BasePersonAdapter } from '@core/data/persons/base-person.model';
import { ReconciliationService } from '@core/data/reconciliation/reconciliation.service';
import { ScrollDispatcher } from '@angular/cdk/scrolling';

@Component({
  selector: 'xt-portal-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  providers: [
    {
      provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
      useValue: { clickAction: 'noop' } as MatCheckboxDefaultOptions,
    },
  ],
  standalone: false,
})
export class TableComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() showDetails: boolean;
  @Input() dataCy: string;
  @Input() disabledMessage: string;
  @Input() allColumns;
  @Input() tabletColumns;
  @Input() mobileColumns;
  @Input() displaySelectedRow: boolean = false;
  @Input() dropdownItems;
  /**
   * use input updateDropdownItems to trigger update dropdown items from dropdownItems input
   */
  updateDropdownItems = input<any>([]);
  @Input() selectedData: any[];
  @Input() data;
  @Input() lockData = false;

  @ViewChild(MatSort) sort: MatSort;
  @Output() onClickEvent: EventEmitter<any> = new EventEmitter();
  @Output() onScrolled = new EventEmitter();
  @Output() selectAllHandler = new EventEmitter();
  @Output() deselectAllHandler = new EventEmitter();
  @Output() toggleSelectHandler = new EventEmitter();
  @Output() clickHandler = new EventEmitter();
  @Output() rowClickHandler = new EventEmitter();

  displayedColumns;
  dataSource: MatTableDataSource<any>;
  isMobile: boolean;
  isTablet: boolean;
  isDropdownIcon = false;
  selectedDataId = '';

  constructor(
    public breakpointObserver: BreakpointObserver,
    private personAdapter: BasePersonAdapter,
    public questionnairesService: QuestionnairesService,
    public reconciliationService: ReconciliationService,
    private scrollDispatcher: ScrollDispatcher,
  ) {}

  ngOnInit() {
    this.updateData();

    this.scrollDispatcher.scrolled().subscribe(() => {
      this.onScrolled.emit();
    });

    // changes columns based on the screen size
    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium])
      .subscribe((state: BreakpointState) => {
        if (state.breakpoints[Breakpoints.XSmall]) {
          this.displayedColumns = this.mobileColumns;
          this.isMobile = true;
          this.isTablet = false;
          this.ngOnChanges(false);
        } else if (state.breakpoints[Breakpoints.Small]) {
          this.displayedColumns = this.tabletColumns;
          this.isTablet = true;
          this.isMobile = false;
          this.ngOnChanges(false);
        } else {
          this.displayedColumns = this.allColumns;
          this.isMobile = false;
          this.isTablet = false;
          this.ngOnChanges(false);
        }
      });
  }

  ngAfterViewInit() {
    this.sortData();
  }

  // changes columns
  ngOnChanges(changes) {
    if (changes.data) {
      this.updateData();
    }

    if (this.showDetails) {
      this.displayedColumns = this.tabletColumns;
    } else if (this.isMobile) {
      this.displayedColumns = this.mobileColumns;
    } else if (this.isTablet) {
      this.displayedColumns = this.tabletColumns;
    } else {
      this.displayedColumns = this.allColumns;
    }
  }

  /**
   * selects item (checkbox)
   *
   * @param id id of selected item
   */
  toggleItem(id) {
    const item = this.data.find((i) => i?.id === id);
    if (item) {
      this.toggleSelectHandler.emit(item);
    }
    this.updateData();
  }

  check(item): boolean {
    return (
      this.selectedData && this.selectedData.some((items) => items.id == item)
    );
  }

  /**
   * selects all items (checkbox)
   *
   */
  selectAll() {
    if (this.data?.length === this.selectedData?.length) {
      this.deselectAllHandler.emit();
    } else {
      this.selectAllHandler.emit();
    }
    this.updateData();
  }

  updateData() {
    const uniqueIds = [];

    const uniques = this.data?.filter((element) => {
      const isDuplicate = uniqueIds.includes(element.practitioner?.id);

      if (!isDuplicate && element.practitioner) {
        uniqueIds.push(element.practitioner?.id);

        return true;
      }

      return false;
    });

    this.data = uniqueIds.length ? uniques : this.data;

    this.dataSource = new MatTableDataSource(this.data);
    this.dataSource.sortingDataAccessor = (item, property) => {
      if (property == 'tags') {
        if (this.getAllTagsByRow(item).length <= 1) {
          return this.getAllTagsByRow(item)[0];
        }
      } else if (isNaN(item[property])) {
        if (Array.isArray(item[property])) {
          return item[property][0].toLocaleLowerCase();
        } else {
          return item[property].toLocaleLowerCase();
        }
      } else {
        return item[property];
      }
    };
    this.sortData();
  }

  sortData() {
    this.dataSource.sort = this.sort;
  }

  onRowClick(row) {
    this.selectedDataId = row.id;

    this.rowClickHandler.emit(row);
  }

  onClickHandler({ methodName, row }) {
    this.onClickEvent.emit({
      methodName: methodName || null,
      row: row || null,
    });
  }

  getPersonGender(person: Person) {
    return this.personAdapter.getHumanGenderTranslated(person);
  }

  getAllTagsByRow(row: DataModel): string[] {
    return Object.entries(row._categories)
      .reduce(
        (acc, [name, categories]) =>
          name.startsWith('_') ? acc : [...acc, ...categories],
        [],
      )
      .map(({ name }) => name);
  }

  getDropDownItemsFiltered(row) {
    return this.dropdownItems
      .map((item) => {
        if (typeof item.disabled === 'function') {
          const disabled = item.disabled(row);
          return { ...item, disabled };
        } else return { ...item };
      })
      .filter((item) => {
        if (item.filterBy)
          return row[item.filterBy.key] === item.filterBy.value;
        return item;
      });
  }

  openLink(link) {
    window.open(link, '_blank', 'noopener,noreferrer');
  }
}
